import { useMutation, useQueryClient } from "react-query";

import axios from "axios";

import { projectServiceEndpoint } from "api/project";

import { PROJECT_LAYER_LIST_QUERY_KEYS } from "components/project/layers/queries";
import {
  DeletableFolderNode,
  collectFolderShapefileIds
} from "components/project/layers/utils/collectCheckedShapefileIds";
import {
  ORGANIZATION_SHAPEFILES_QUERY_KEYS,
  PROJECT_SHAPEFILES_QUERY_KEYS,
  SYSTEM_SHAPEFILES_QUERY_KEYS
} from "components/project/shapefiles/queries";
import { usePurgeDeletedShapefilesFromDefaults } from "components/user-settings/hooks";
import {
  DEFAULTS_PURGE_FAILURE_MESSAGES,
  reportDefaultsPurgeFailure
} from "components/user-settings/hooks/reportDefaultsPurgeFailure";

interface IUseDeleteProjectMutationParams {
  onError?: (error) => void;
  onMutate?: () => void;
  onSettled?: () => void;
  onSuccess?: (data) => void;
  projectId: string;
}

interface IDeleteProjectShapefileFolderParams {
  shapefileNodeId: string;
  projectId?: string;
  // The folder node with its children, from whichever tree the caller renders.
  // The delete endpoint cascade-deletes the subtree but identifies it only by
  // shapefileNodeId, so without this the deleted shapefiles' fields cannot be
  // purged from the user's saved defaults (EVA-126).
  folderNode?: DeletableFolderNode;
}

export async function deleteProjectShapefileFolder(
  params: IDeleteProjectShapefileFolderParams
) {
  const url =
    params.projectId === "system"
      ? `${projectServiceEndpoint}/system-shapefile-node/${params.shapefileNodeId}`
      : `${projectServiceEndpoint}/shapefile-node/${params.shapefileNodeId}`;
  return await axios.delete(url);
}

export function useDeleteProjectShapefileFolderMutation({
  onError,
  onMutate,
  onSettled,
  onSuccess,
  projectId
}: IUseDeleteProjectMutationParams) {
  const queryClient = useQueryClient();
  const purgeDeletedShapefilesFromDefaults = usePurgeDeletedShapefilesFromDefaults();

  return useMutation(
    (params: IDeleteProjectShapefileFolderParams) => deleteProjectShapefileFolder(params),
    {
      onMutate: onMutate && onMutate,
      onSuccess: (data, variables) => {
        if (projectId === "system") {
          queryClient.invalidateQueries({
            queryKey: [SYSTEM_SHAPEFILES_QUERY_KEYS.systemShapefiles]
          });
        } else if (projectId === "organization") {
          queryClient.invalidateQueries({
            queryKey: [ORGANIZATION_SHAPEFILES_QUERY_KEYS.organizationShapefiles]
          });
          queryClient.invalidateQueries({
            queryKey: [PROJECT_SHAPEFILES_QUERY_KEYS.currentProjectShapefiles]
          });

          queryClient.invalidateQueries({
            queryKey: [PROJECT_LAYER_LIST_QUERY_KEYS.currentProjectLayerList]
          });
        }

        queryClient.invalidateQueries({
          queryKey: [PROJECT_SHAPEFILES_QUERY_KEYS.currentProjectShapefiles, projectId]
        });

        queryClient.invalidateQueries({
          queryKey: [PROJECT_LAYER_LIST_QUERY_KEYS.currentProjectLayerList, projectId]
        });

        // The cascade deletes every owned shapefile in the subtree, and their
        // fields linger in the user's saved focus/tooltip lists (EVA-126).
        // Best-effort - never block the caller's onSuccess below.
        try {
          purgeDeletedShapefilesFromDefaults(
            collectFolderShapefileIds(variables?.folderNode)
          );
        } catch (error) {
          reportDefaultsPurgeFailure(
            DEFAULTS_PURGE_FAILURE_MESSAGES.shapefilePurgeFailed,
            error
          );
        }

        onSuccess && onSuccess(data);
      },
      onError: (error) => {
        onError && onError(error);
      },
      onSettled: onSettled && onSettled
    }
  );
}
